import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ShoppingCart, Package, CheckCircle, Clock, XCircle,
    Eye, DollarSign, AlertCircle, Search, Filter,
    Download, ChevronDown, Check, ExternalLink, BadgePercent,
    FileText, User, CreditCard, MapPin, Phone, Mail,
    Building2, Zap, FileImage, Plus, RefreshCw, Calendar
} from 'lucide-react';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import DataTable from '../components/dashboard/DataTable';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

const DetailRow = ({ label, value, icon: Icon }) => (
    <div className="flex justify-between items-center py-1.5">
        <span className="text-muted-foreground text-sm flex items-center gap-1.5">
            {Icon && <Icon className="w-3.5 h-3.5" />}
            {label}
        </span>
        <span className="font-medium text-sm text-right max-w-[60%] break-words">{value || '—'}</span>
    </div>
);

const DocLink = ({ url, label }) => {
    if (!url) return <span className="text-xs text-muted-foreground">Not uploaded</span>;
    const baseUrl = API.endsWith('/') ? API.slice(0, -1) : API;
    const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
    return (
        <a href={fullUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
            <FileImage className="w-3 h-3" />
            {label}
            <ExternalLink className="w-3 h-3" />
        </a>
    );
};

const AdminSubsidyOrders = () => {
    const { token } = useAuth();
    const [orders, setOrders] = useState([]);
    const [stats, setStats] = useState({ total: 0, pending: 0, under_review: 0, approved: 0, rejected: 0, disbursed: 0 });
    const [loading, setLoading] = useState(true);
    const [seedingTest, setSeedingTest] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [newStatus, setNewStatus] = useState('');
    const [statusNotes, setStatusNotes] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        fetchSubsidyOrders();
        fetchStats();
    }, []);

    const fetchSubsidyOrders = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/api/admin/subsidy-orders`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setOrders(res.data);
        } catch (error) {
            console.error('Failed to fetch subsidy orders', error);
            setOrders([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const res = await axios.get(`${API}/api/admin/subsidy-orders/stats`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setStats(res.data);
        } catch (error) {
            console.error('Failed to fetch stats', error);
        }
    };

    const handleSeedTest = async () => {
        setSeedingTest(true);
        try {
            const res = await axios.post(`${API}/api/admin/subsidy-orders/seed-test`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(`Test order created: ${res.data.order_id}`);
            fetchSubsidyOrders();
            fetchStats();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to seed test data');
        } finally {
            setSeedingTest(false);
        }
    };

    const handleUpdateStatus = async () => {
        if (!newStatus) {
            toast.error('Please select a status');
            return;
        }
        try {
            await axios.put(
                `${API}/api/admin/subsidy-orders/${selectedOrder.id}/status`,
                { new_status: newStatus, notes: statusNotes || '' },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`Status updated to ${newStatus.replaceAll('_', ' ')}`);
            setShowStatusModal(false);
            setStatusNotes('');
            fetchSubsidyOrders();
            fetchStats();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update status');
        }
    };

    const filteredOrders = orders.filter(order => {
        const matchesStatus = statusFilter === 'all' || order.subsidy_status === statusFilter;
        const searchLower = searchQuery.toLowerCase();
        const matchesSearch = !searchQuery ||
            order.id?.toLowerCase().includes(searchLower) ||
            order.customer_name?.toLowerCase().includes(searchLower) ||
            order.customer_email?.toLowerCase().includes(searchLower) ||
            order.subsidy_details?.aadhaar_name?.toLowerCase().includes(searchLower) ||
            order.subsidy_details?.aadhaar_number?.includes(searchLower) ||
            order.subsidy_details?.pan_number?.toLowerCase().includes(searchLower);
        return matchesStatus && matchesSearch;
    });

    const getStatusColor = (status) => {
        switch (status) {
            case 'approved': return 'bg-green-100 text-green-700 border-green-200';
            case 'disbursed': return 'bg-blue-100 text-blue-700 border-blue-200';
            case 'rejected': return 'bg-red-100 text-red-700 border-red-200';
            case 'under_review': return 'bg-amber-100 text-amber-700 border-amber-200';
            case 'submitted': return 'bg-purple-100 text-purple-700 border-purple-200';
            default: return 'bg-gray-100 text-gray-700 border-gray-200';
        }
    };

    const statCards = [
        { label: 'Total Applications', value: stats.total, icon: BadgePercent, color: 'text-primary' },
        { label: 'Pending', value: stats.pending, icon: Clock, color: 'text-gray-500' },
        { label: 'Under Review', value: stats.under_review, icon: Search, color: 'text-amber-500' },
        { label: 'Approved', value: stats.approved, icon: CheckCircle, color: 'text-green-500' },
        { label: 'Rejected', value: stats.rejected, icon: XCircle, color: 'text-red-500' },
        { label: 'Disbursed', value: stats.disbursed, icon: DollarSign, color: 'text-blue-500' },
    ];

    const columns = [
        {
            header: 'Order ID',
            accessor: 'id',
            render: (val) => <span className="font-mono text-xs">{val?.length > 20 ? val.slice(0, 20) + '...' : val}</span>
        },
        {
            header: 'Applicant',
            accessor: 'subsidy_details',
            render: (val, row) => (
                <div>
                    <p className="font-medium">{val?.aadhaar_name || row.customer_name || 'N/A'}</p>
                    <p className="text-xs text-muted-foreground">{row.customer_email || val?.email || ''}</p>
                </div>
            )
        },
        {
            header: 'Product',
            accessor: 'items',
            render: (val) => (
                <span className="text-xs">{val?.[0]?.name || 'N/A'}</span>
            )
        },
        {
            header: 'Subsidy',
            accessor: 'subsidy_amount',
            render: (val) => <span className="font-bold text-green-600">₹{(val || 0).toLocaleString()}</span>
        },
        {
            header: 'Status',
            accessor: 'subsidy_status',
            render: (val) => (
                <Badge variant="outline" className={getStatusColor(val)}>
                    {(val || 'pending').replaceAll('_', ' ').toUpperCase()}
                </Badge>
            )
        },
        {
            header: 'Date',
            accessor: 'created_at',
            render: (val) => <span className="text-xs text-muted-foreground">{val ? new Date(val).toLocaleDateString() : 'N/A'}</span>
        },
        {
            header: 'Actions',
            accessor: 'actions',
            render: (_, row) => (
                <div className="flex gap-2">
                    <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setSelectedOrder(row)}
                        title="View Details"
                    >
                        <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                            setSelectedOrder(row);
                            setNewStatus(row.subsidy_status || 'pending');
                            setShowStatusModal(true);
                        }}
                    >
                        Update
                    </Button>
                </div>
            )
        }
    ];


    return (
        <DashboardLayout userRole="admin">
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <BadgePercent className="w-6 h-6 text-primary" />
                            Subsidy Applications
                        </h1>
                        <p className="text-muted-foreground">Manage PM Surya Ghar subsidy claims</p>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => { fetchSubsidyOrders(); fetchStats(); }}>
                            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                        </Button>
                        <Button size="sm" onClick={handleSeedTest} disabled={seedingTest} className="btn-primary">
                            <Plus className="w-4 h-4 mr-2" /> {seedingTest ? 'Creating...' : 'Seed Test Order'}
                        </Button>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    {statCards.map((stat, i) => (
                        <motion.div
                            key={stat.label}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.05 }}
                            className="bg-card rounded-xl border p-4 text-center"
                        >
                            <stat.icon className={`w-5 h-5 mx-auto mb-1 ${stat.color}`} />
                            <p className="text-2xl font-bold">{stat.value}</p>
                            <p className="text-xs text-muted-foreground">{stat.label}</p>
                        </motion.div>
                    ))}
                </div>

                {/* Filters */}
                <div className="flex flex-col md:flex-row gap-4 bg-card p-4 rounded-xl border">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            placeholder="Search by Order ID, Name, Email, Aadhaar, PAN..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9"
                        />
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-[180px]">
                            <SelectValue placeholder="All Status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Status</SelectItem>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="submitted">Submitted</SelectItem>
                            <SelectItem value="under_review">Under Review</SelectItem>
                            <SelectItem value="approved">Approved</SelectItem>
                            <SelectItem value="disbursed">Disbursed</SelectItem>
                            <SelectItem value="rejected">Rejected</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Orders Table */}
                <DataTable
                    columns={columns}
                    data={filteredOrders}
                    loading={loading}
                    emptyMessage="No subsidy applications found. Click 'Seed Test Order' to create one."
                />

                {/* ===== Order Detail Modal ===== */}
                <Dialog open={!!selectedOrder && !showStatusModal} onOpenChange={(open) => !open && setSelectedOrder(null)}>
                    <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <BadgePercent className="w-5 h-5 text-primary" />
                                Subsidy Application: {selectedOrder?.id}
                            </DialogTitle>
                            <DialogDescription>
                                Submitted on {selectedOrder?.created_at ? new Date(selectedOrder.created_at).toLocaleString() : 'N/A'}
                            </DialogDescription>
                        </DialogHeader>

                        {selectedOrder && (() => {
                            const sd = selectedOrder.subsidy_details || {};
                            return (
                                <div className="space-y-6 py-4">
                                    {/* Status Banner */}
                                    <div className="flex items-center justify-between bg-gradient-to-r from-primary/5 to-accent/5 border rounded-xl p-4">
                                        <div>
                                            <p className="text-sm text-muted-foreground">Current Status</p>
                                            <Badge variant="outline" className={`mt-1 ${getStatusColor(selectedOrder.subsidy_status)}`}>
                                                {(selectedOrder.subsidy_status || 'pending').replaceAll('_', ' ').toUpperCase()}
                                            </Badge>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-sm text-muted-foreground">Subsidy Amount</p>
                                            <p className="text-2xl font-bold text-green-600">₹{(selectedOrder.subsidy_amount || 0).toLocaleString()}</p>
                                        </div>
                                    </div>

                                    <div className="grid md:grid-cols-2 gap-6">
                                        {/* Personal Information */}
                                        <div className="space-y-3">
                                            <h3 className="font-semibold flex items-center gap-2 text-primary border-b pb-2">
                                                <User className="w-4 h-4" /> Personal Information
                                            </h3>
                                            <div className="bg-secondary/30 p-4 rounded-lg space-y-1">
                                                <DetailRow label="Name (Aadhaar)" value={sd.aadhaar_name} />
                                                <DetailRow label="Aadhaar Number" value={sd.aadhaar_number} />
                                                <DetailRow label="PAN Number" value={sd.pan_number} />
                                                <DetailRow label="Mobile" value={sd.mobile_number} icon={Phone} />
                                                <DetailRow label="Email" value={sd.email} icon={Mail} />
                                                <DetailRow label="Date of Birth" value={sd.date_of_birth} icon={Calendar} />
                                            </div>
                                        </div>

                                        {/* Installation Address */}
                                        <div className="space-y-3">
                                            <h3 className="font-semibold flex items-center gap-2 text-primary border-b pb-2">
                                                <MapPin className="w-4 h-4" /> Installation Address
                                            </h3>
                                            <div className="bg-secondary/30 p-4 rounded-lg space-y-1">
                                                <DetailRow label="Address" value={sd.installation_address} />
                                                <DetailRow label="State" value={sd.state} />
                                                <DetailRow label="District" value={sd.district} />
                                                <DetailRow label="PIN Code" value={sd.pincode} />
                                            </div>
                                        </div>

                                        {/* Electricity Connection */}
                                        <div className="space-y-3">
                                            <h3 className="font-semibold flex items-center gap-2 text-primary border-b pb-2">
                                                <Zap className="w-4 h-4" /> Electricity Connection
                                            </h3>
                                            <div className="bg-secondary/30 p-4 rounded-lg space-y-1">
                                                <DetailRow label="Consumer Number" value={sd.electricity_consumer_number} />
                                                <DetailRow label="DISCOM" value={sd.discom_name} />
                                                <DetailRow label="Connection Type" value={sd.connection_type?.replaceAll('_', ' ')} />
                                                <DetailRow label="Sanctioned Load" value={sd.sanctioned_load ? `${sd.sanctioned_load} kW` : null} />
                                                <DetailRow label="Property Ownership" value={sd.property_ownership?.replaceAll('_', ' ')} />
                                                <DetailRow label="Avg Monthly Bill" value={sd.avg_monthly_bill ? `₹${sd.avg_monthly_bill}` : null} />
                                            </div>
                                        </div>

                                        {/* Bank Details */}
                                        <div className="space-y-3">
                                            <h3 className="font-semibold flex items-center gap-2 text-primary border-b pb-2">
                                                <CreditCard className="w-4 h-4" /> Bank Account
                                            </h3>
                                            <div className="bg-secondary/30 p-4 rounded-lg space-y-1">
                                                <DetailRow label="Account Holder" value={sd.bank_account_name} />
                                                <DetailRow label="Account Number" value={sd.bank_account_number} />
                                                <DetailRow label="IFSC Code" value={sd.bank_ifsc_code} />
                                                <DetailRow label="Bank Name" value={sd.bank_name} />
                                                <DetailRow label="Branch" value={sd.bank_branch} />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Uploaded Documents */}
                                    <div className="space-y-3">
                                        <h3 className="font-semibold flex items-center gap-2 text-primary border-b pb-2">
                                            <FileText className="w-4 h-4" /> Uploaded Documents
                                        </h3>
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                            {[
                                                { key: 'aadhaar_doc', label: 'Aadhaar Card' },
                                                { key: 'pan_doc', label: 'PAN Card' },
                                                { key: 'electricity_bill_doc', label: 'Electricity Bill' },
                                                { key: 'bank_passbook_doc', label: 'Bank Passbook' },
                                                { key: 'property_proof_doc', label: 'Property Proof' },
                                                { key: 'photo_doc', label: 'Photo' }
                                            ].map(doc => (
                                                <div key={doc.key} className={`p-3 rounded-lg border text-center ${sd[doc.key] ? 'bg-green-50 dark:bg-green-950/20 border-green-200' : 'bg-red-50 dark:bg-red-950/20 border-red-200'}`}>
                                                    <p className="text-xs font-medium mb-1">{doc.label}</p>
                                                    {sd[doc.key] ? (
                                                        <DocLink url={sd[doc.key]} label="View" />
                                                    ) : (
                                                        <span className="text-xs text-red-500">Not Uploaded</span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Order Details */}
                                    <div className="space-y-3">
                                        <h3 className="font-semibold flex items-center gap-2 text-primary border-b pb-2">
                                            <Package className="w-4 h-4" /> Order Details
                                        </h3>
                                        <div className="bg-secondary/30 p-4 rounded-lg space-y-1">
                                            <DetailRow label="Order Total" value={`₹${(selectedOrder.total_amount || 0).toLocaleString()}`} />
                                            <DetailRow label="Order Status" value={selectedOrder.status?.toUpperCase()} />
                                            <DetailRow label="Payment Method" value={selectedOrder.payment_method} />
                                            <DetailRow label="Payment Status" value={selectedOrder.payment_status?.toUpperCase()} />
                                            {selectedOrder.items?.map((item, i) => (
                                                <DetailRow key={i} label={`Product ${i + 1}`} value={`${item.name} × ${item.quantity}`} icon={Package} />
                                            ))}
                                        </div>
                                    </div>

                                    {/* Admin Notes */}
                                    {selectedOrder.subsidy_notes && (
                                        <div className="space-y-3">
                                            <h3 className="font-semibold flex items-center gap-2 text-amber-600 border-b pb-2">
                                                <AlertCircle className="w-4 h-4" /> Admin Notes
                                            </h3>
                                            <div className="bg-amber-50 dark:bg-amber-950/20 p-4 rounded-lg border border-amber-200">
                                                <p className="text-sm italic">{selectedOrder.subsidy_notes}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })()}

                        <DialogFooter>
                            <Button variant="outline" onClick={() => setSelectedOrder(null)}>Close</Button>
                            <Button onClick={() => {
                                setNewStatus(selectedOrder?.subsidy_status || 'pending');
                                setShowStatusModal(true);
                            }}>
                                Update Status
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Update Status Modal */}
                <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Update Application Status</DialogTitle>
                            <DialogDescription>Change status for Order #{selectedOrder?.id}</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label>New Status</Label>
                                <Select value={newStatus} onValueChange={setNewStatus}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select Status" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="pending">Pending</SelectItem>
                                        <SelectItem value="submitted">Submitted to MNRE</SelectItem>
                                        <SelectItem value="under_review">Under Review</SelectItem>
                                        <SelectItem value="approved">Approved</SelectItem>
                                        <SelectItem value="rejected">Rejected</SelectItem>
                                        <SelectItem value="disbursed">Disbursed</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Notes / Comments</Label>
                                <Textarea
                                    value={statusNotes}
                                    onChange={(e) => setStatusNotes(e.target.value)}
                                    placeholder="Add notes about this status change..."
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setShowStatusModal(false)}>Cancel</Button>
                            <Button onClick={handleUpdateStatus}>Update Status</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </DashboardLayout>
    );
};

export default AdminSubsidyOrders;
